import { AppBar, Toolbar, IconButton, useMediaQuery, Box, Fade } from '@mui/material';
import styled, { css, keyframes } from 'styled-components';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ArrowBack, Search } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { isDevEnvironment } from '../../../shared/utils/configuration';
import { Link } from 'react-router-dom';
import OrderSearch from './OrderSearch';
import { useCanViewOrders } from '../../../shared/utils/permissions';

type SearchPhase = 'closed' | 'open' | 'closing';

const LOGO_URL = 'https://www.kotipizza.fi/Kotipizza_Logo.svg';
const DEV_LOGO_URL = 'https://mob-cdn.azureedge.net/images/devipizza.svg';

// The mobile search hides behind the magnifier and takes the whole bar when opened. 'closing' keeps it
// mounted while its exit animation plays.
const useMobileSearch = (enabled: boolean) => {
  const [phase, setPhase] = useState<SearchPhase>('closed');

  // With reduced motion there is no exit animation to wait for.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const close = useCallback(() => setPhase(prefersReducedMotion ? 'closed' : 'closing'), [prefersReducedMotion]);
  const finishClosing = useCallback(() => setPhase('closed'), []);

  // Keys the search layer, so reopening mid-close mounts a fresh, empty, autofocused field.
  const [key, setKey] = useState(0);
  const open = () => {
    setKey((current) => current + 1);
    setPhase('open');
  };

  // Otherwise a search opened on a phone would reopen by itself after resizing to desktop and back.
  useEffect(() => {
    if (!enabled) setPhase('closed');
  }, [enabled]);

  return {
    key,
    isMounted: enabled && phase !== 'closed',
    isExpanded: enabled && phase === 'open',
    isClosing: phase === 'closing',
    prefersReducedMotion,
    open,
    close,
    finishClosing,
  };
};

type Props = {
  handleDrawerToggle: () => void;
  isOpen: boolean;
};

const TopBar: React.FC<Props> = ({ handleDrawerToggle, isOpen }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const canViewOrders = useCanViewOrders();
  const search = useMobileSearch(isMobile && canViewOrders);

  return (
    <StyledTopBar>
      <AppBar className={classes.appBar}>
        <Toolbar>
          {/* Laid over the bar rather than swapped in, so it has something to grow over. */}
          {search.isMounted && (
            <MobileSearch
              key={search.key}
              isClosing={search.isClosing}
              onClose={search.close}
              onClosed={search.finishClosing}
            />
          )}
          {/* Once faded out, `Fade` hides it, taking its buttons out of the tab order. */}
          <Fade in={!search.isExpanded} timeout={search.prefersReducedMotion ? 0 : undefined}>
            <ToolbarContent>
              <MenuToggleButton isOpen={isOpen} onClick={handleDrawerToggle} />
              <Box display="flex" alignItems="center" flex="1">
                {!isMobile && <StyledOrderSearch />}
              </Box>
              <CenteredLogoLink to="/">
                <Logo src={isDevEnvironment() ? DEV_LOGO_URL : LOGO_URL} />
              </CenteredLogoLink>
              {/* Stands in for the desktop search box, which does not fit beside the logo. Tapping it hands
                  the whole bar over to the field. */}
              {isMobile && canViewOrders && (
                <IconButton aria-label={t('global.search')} color="inherit" size="large" onClick={search.open}>
                  <Search />
                </IconButton>
              )}
            </ToolbarContent>
          </Fade>
        </Toolbar>
      </AppBar>
    </StyledTopBar>
  );
};

const MenuToggleButton: React.FC<{ isOpen: boolean; onClick: () => void }> = ({ isOpen, onClick }) => (
  <IconButton className={classes.menuButton} edge="start" color="inherit" onClick={onClick} size="large">
    <MenuIconWrapper $isOpen={isOpen}>
      <MenuIcon className="menu-icon" />
      <CloseIcon className="close-icon" />
    </MenuIconWrapper>
  </IconButton>
);

type MobileSearchProps = {
  isClosing: boolean;
  onClose: () => void;
  /** The exit animation finished. */
  onClosed: () => void;
};

const MobileSearch: React.FC<MobileSearchProps> = ({ isClosing, onClose, onClosed }) => {
  const { t } = useTranslation();

  return (
    <SearchLayer
      $isClosing={isClosing}
      onAnimationEnd={(e) => {
        // Children's animations bubble up here too.
        if (e.target === e.currentTarget && isClosing) onClosed();
      }}
    >
      <IconButton
        className={`${classes.menuButton} search-back`}
        edge="start"
        color="inherit"
        size="large"
        aria-label={t('global.back')}
        onClick={onClose}
      >
        <ArrowBack />
      </IconButton>
      <Box className="search-field" alignItems="center" display="flex" flex="1">
        <StyledMobileOrderSearch autoFocus onSubmitted={onClose} onDismiss={onClose} />
      </Box>
    </SearchLayer>
  );
};

export default TopBar;

// A fixed height, so the image doesn't resize the bar when it finishes loading.
const Logo = styled.img`
  height: 38px;
`;

// Positioned on the toolbar, so it centres on the bar even though the buttons either side differ in width.
// `50vw` rather than `50%`: the fixed bar narrows by the page's scrollbar, which would nudge the logo
// whenever a page grows long enough to scroll.
const CenteredLogoLink = styled(Link)`
  position: absolute;
  left: 50vw;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
`;

const ToolbarContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const PREFIX = 'TopBar';
const classes = {
  appBar: `${PREFIX}-appBar`,
  menuButton: `${PREFIX}-menuButton`,
};

const StyledTopBar = styled.div(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    backgroundColor: theme.palette.navigation.main,
    zIndex: theme.zIndex.drawer + 1,
  },
  // One height, whatever the bar happens to be holding — the logo or a search field.
  // Swapping the row's contents must not move the page underneath it.
  //
  // `theme.spacing(8)` rather than MUI's own toolbar minimum, which is 56px below 600px and 48px on a
  // landscape phone: `Main` pushes the content down by spacing(8) at every width, and
  // `OrderDetailsDrawer` measures its panel against the same, so the bar is the one that was out of
  // step. `minHeight`, not `height`, so content that genuinely needs more room still gets it.
  [`& .${classes.appBar} .MuiToolbar-root`]: {
    minHeight: theme.spacing(8),
  },
  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
  },
}));

const pillSearch = css`
  /* \`index.css\` paints every \`.MuiTextField-root\` white, which shows as a square inside the pill. */
  .MuiTextField-root {
    background-color: transparent;
  }

  /* Tucks the search button into the pill's round end, where the bar's magnifier sat on mobile. */
  .MuiOutlinedInput-root {
    border-radius: 999px;
    padding-right: 4px;
    background-color: ${({ theme }) => theme.palette.common.white};
  }

  .MuiOutlinedInput-notchedOutline,
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: transparent;
  }

  .MuiInputAdornment-root .MuiIconButton-root {
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`;

// Stops short of the centred logo on narrow desktops: 88px of gutter and hamburger to its left, then half
// the ~61px logo and a gap.
const StyledOrderSearch = styled(OrderSearch)`
  width: 100%;
  max-width: min(25rem, calc(50vw - 136px));
  ${pillSearch}
`;

const MenuIconWrapper = styled.span<{ $isOpen: boolean }>`
  position: relative;
  display: inline-flex;
  width: 24px;
  height: 24px;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .menu-icon {
    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
    transform: rotate(${({ $isOpen }) => ($isOpen ? 90 : 0)}deg);
  }

  .close-icon {
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    transform: rotate(${({ $isOpen }) => ($isOpen ? 0 : -90)}deg);
  }
`;

// Scaled rather than clipped: a `clip-path` animation runs on the main thread and could freeze halfway while the
// field's autofocus opens the keyboard.
const revealSearch = keyframes`
  from { transform: scale(0); }
`;

const concealSearch = keyframes`
  to { transform: scale(0); }
`;

const enterBackButton = keyframes`
  from { opacity: 0; transform: rotate(-90deg); }
  to { opacity: 1; transform: rotate(0); }
`;

// Stretches leftwards out of its search button (40px + the pill's 4px padding), so the icon never moves.
const enterSearchField = keyframes`
  from { clip-path: inset(0 0 0 calc(100% - 44px) round 999px); }
  to { clip-path: inset(0 0 0 0 round 999px); }
`;

const fadeOut = keyframes`
  to { opacity: 0; }
`;

// Covers the toolbar and repeats its gutters, so the Back arrow lands where the hamburger was.
const SearchLayer = styled.div<{ $isClosing: boolean }>(
  ({ theme, $isClosing }) => css`
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;
    display: flex;
    align-items: center;
    padding: 0 ${theme.spacing(2)};
    --search-origin-x: calc(${theme.spacing(2)} + 24px);

    ${theme.breakpoints.up('sm')} {
      padding: 0 ${theme.spacing(3)};
      --search-origin-x: calc(${theme.spacing(3)} + 24px);
    }

    /* The bar's background, as a circle centred on the magnifier (the gutter plus half the 48px button in
       from the right) and wide enough to cover the bar from there. */
    &::before {
      content: '';
      position: absolute;
      z-index: -1;
      width: 200vw;
      height: 200vw;
      right: calc(var(--search-origin-x) - 100vw);
      top: calc(50% - 100vw);
      border-radius: 50%;
      background-color: ${theme.palette.navigation.main};
      animation: ${revealSearch} ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeOut} both;
    }

    .search-back {
      animation: ${enterBackButton} ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut} 100ms both;
    }

    .search-field {
      animation: ${enterSearchField} ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut} 80ms both;
    }

    ${$isClosing &&
    css`
      pointer-events: none;

      &::before {
        animation: ${concealSearch} ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp} both;
      }

      .search-back,
      .search-field {
        animation: ${fadeOut} ${theme.transitions.duration.shortest}ms linear both;
      }
    `}

    @media (prefers-reduced-motion: reduce) {
      &::before,
      .search-back,
      .search-field {
        animation: none;
      }
    }
  `
);

const StyledMobileOrderSearch = styled(OrderSearch)`
  width: 100%;
  ${pillSearch}
`;
